import React from 'react';
import type { ManualShiftEntry } from '../../shifts/api/manualShiftsApi';
import type { DemoRoute } from '../demoMode';
import type { FeatureTour } from '../tourTypes';

const ShiftsPage = React.lazy(() => import('../../shifts/ShiftsPage'));

// Invented demo data only — no real person or agency.
const DAY = 86400_000;
const today = () => {
  const d = new Date();
  return Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
};
const iso = (ms: number) => new Date(ms).toISOString().slice(0, 10);

const PATIENTS = [
  { id: 'p1', firstName: 'Josefine', lastName: 'Huber', clientId: 'K-1001', addressStreet: 'Lindengasse', addressHouseNumber: '12', addressPostalCode: '1070', addressCity: 'Wien', phoneNumberMobile: '+43 660 0000001', isActive: true },
  { id: 'p2', firstName: 'Karl', lastName: 'Wagner', clientId: 'K-1002', addressStreet: 'Hauptplatz', addressHouseNumber: '3', addressPostalCode: '3100', addressCity: 'St. Pölten', phoneNumberMobile: '+43 660 0000002', isActive: true },
  { id: 'p3', firstName: 'Hermine', lastName: 'Steiner', clientId: 'K-1003', addressStreet: 'Bergstraße', addressHouseNumber: '7', addressPostalCode: '4020', addressCity: 'Linz', phoneNumberMobile: '+43 660 0000003', isActive: true },
  { id: 'p4', firstName: 'Franz', lastName: 'Gruber', clientId: 'K-1004', addressStreet: 'Am Anger', addressHouseNumber: '21', addressPostalCode: '8010', addressCity: 'Graz', phoneNumberMobile: '+43 660 0000004', isActive: true },
  { id: 'p5', firstName: 'Theresia', lastName: 'Bauer', clientId: 'K-1005', addressStreet: 'Seeweg', addressHouseNumber: '5', addressPostalCode: '5020', addressCity: 'Salzburg', phoneNumberMobile: '+43 660 0000005', isActive: true },
  { id: 'p6', firstName: 'Leopold', lastName: 'Moser', clientId: 'K-1006', addressStreet: 'Kirchengasse', addressHouseNumber: '9', addressPostalCode: '6020', addressCity: 'Innsbruck', phoneNumberMobile: '+43 660 0000006', isActive: true },
];

const CAREGIVERS = [
  { id: 'c1', caregiverId: 'B-201', firstName: 'Ana', lastName: 'Popescu', phoneNumber: '+40 700 000001', isActive: true },
  { id: 'c2', caregiverId: 'B-202', firstName: 'Zuzana', lastName: 'Kováčová', phoneNumber: '+421 900 000002', isActive: true },
  { id: 'c3', caregiverId: 'B-203', firstName: 'Marta', lastName: 'Nowak', phoneNumber: '+48 500 000003', isActive: true },
  { id: 'c4', caregiverId: 'B-204', firstName: 'Ioana', lastName: 'Dumitru', phoneNumber: '+40 700 000004', isActive: true },
  { id: 'c5', caregiverId: 'B-205', firstName: 'Eva', lastName: 'Horváth', phoneNumber: '+36 30 0000005', isActive: true },
  { id: 'c6', caregiverId: 'B-206', firstName: 'Jana', lastName: 'Novotná', phoneNumber: '+420 600 000006', isActive: true },
  { id: 'c7', caregiverId: 'B-207', firstName: 'Maria', lastName: 'Ionescu', phoneNumber: '+40 700 000007', isActive: true },
  { id: 'c8', caregiverId: 'B-208', firstName: 'Katarína', lastName: 'Horná', phoneNumber: '+421 900 000008', isActive: true },
];

const COLORS = ['#1677ff', '#52c41a', '#722ed1', '#fa8c16', '#13c2c2', '#eb2f96'];

/**
 * Per client: two caregivers alternating in 4-week turnusse, each handover on a
 * shared changeover day. Offsets differ so handovers spread over the weeks.
 * Theresia Bauer has an uncovered week, Leopold Moser a turnus still unassigned.
 */
const PLAN: Array<{ patient: string; pair: [string, string]; offset: number; turnusDays: number; gapAfter?: number; unassigned?: number }> = [
  { patient: 'p1', pair: ['c1', 'c2'], offset: -20, turnusDays: 28 },
  { patient: 'p2', pair: ['c3', 'c4'], offset: -8, turnusDays: 28 },
  { patient: 'p3', pair: ['c5', 'c6'], offset: -26, turnusDays: 14 },
  { patient: 'p4', pair: ['c7', 'c8'], offset: -3, turnusDays: 28 },
  { patient: 'p5', pair: ['c2', 'c5'], offset: -15, turnusDays: 21, gapAfter: 1 },
  { patient: 'p6', pair: ['c4', 'c1'], offset: -12, turnusDays: 28, unassigned: 2 },
];

const name = (list: Array<{ id: string; firstName: string; lastName: string }>, id?: string) => {
  const p = list.find((x) => x.id === id);
  return p ? `${p.firstName} ${p.lastName}` : '';
};

const buildEntries = (): ManualShiftEntry[] => {
  const t0 = today();
  const windowStart = t0 - 70 * DAY;
  const windowEnd = t0 + 160 * DAY;
  const out: ManualShiftEntry[] = [];
  let caseNumber = 1040;
  PLAN.forEach((plan, pi) => {
    const patient = PATIENTS.find((p) => p.id === plan.patient)!;
    let start = t0 + (plan.offset - plan.turnusDays * 3) * DAY;
    for (let n = 0; start < windowEnd; n++) {
      const end = start + plan.turnusDays * DAY;
      const caregiverId = plan.unassigned === n - 3 ? undefined : plan.pair[n % 2];
      const shiftId = `demo-${plan.patient}-${n}`;
      caseNumber += 1;
      const past = end < t0;
      for (let d = Math.max(start, windowStart); d <= Math.min(end, windowEnd); d += DAY) {
        out.push({
          shiftId,
          patientId: patient.id,
          patientName: `${patient.lastName} ${patient.firstName}`,
          patientFirstName: patient.firstName,
          patientLastName: patient.lastName,
          caregiverId,
          caregiverName: caregiverId ? name(CAREGIVERS, caregiverId) : undefined,
          caseNumber,
          dailyRate: 85,
          clientDailyRate: 95,
          date: iso(d),
          status: 'manual',
          color: COLORS[pi % COLORS.length],
          invoiceEnabled: true,
          invoiceAutoSend: false,
          invoiceSentAt: past ? new Date(end).toISOString() : null,
          invoiceNumber: past ? `RE-2026-${caseNumber}` : null,
          invoiceStatus: past ? 'paid' : null,
          assignedStaffName: 'Sabine Demo',
          shiftStatus: past ? 'completed' : 'pending',
          kontrolliert: past,
          patientIsActive: true,
          originalStartDate: iso(start),
          originalEndDate: iso(end),
        });
      }
      // a week without care after the gap turnus, otherwise a shared changeover day
      start = plan.gapAfter === n - 3 ? end + 7 * DAY : end;
    }
  });
  return out;
};

let entries: ManualShiftEntry[] = [];
const resetDemoState = () => {
  entries = buildEntries();
};

const TurnusPreviewPage: React.FC = () => {
  // Dates are relative to today, built fresh per preview mount.
  React.useState(resetDemoState);
  return React.createElement(ShiftsPage);
};

const paginated = <T,>(data: T[]) => ({ data, total: data.length, page: 1, limit: 1000, totalPages: 1 });
const ok = (data: unknown) => ({ success: true, data });

const caseNumbers = () => {
  const map = new Map<number, { caseNumber: number; patientId: string; patientName: string; caregiverNames: Set<string> }>();
  entries.forEach((e) => {
    const c = map.get(e.caseNumber) || { caseNumber: e.caseNumber, patientId: e.patientId, patientName: e.patientName, caregiverNames: new Set<string>() };
    if (e.caregiverName) c.caregiverNames.add(e.caregiverName);
    map.set(e.caseNumber, c);
  });
  return Array.from(map.values()).map((c) => ({ ...c, caregiverNames: Array.from(c.caregiverNames), assignedStaffNames: ['Sabine Demo'] }));
};

const mocks: DemoRoute[] = [
  { path: /^\/manual-shifts$/, reply: () => ({ data: entries, total: PATIENTS.length, hasMore: false }) },
  { path: /^\/manual-shifts\/all-conflicts$/, reply: () => ({ status: 'success', message: '', data: [], totalConflicts: 0 }) },
  { path: /^\/manual-shifts\/case-numbers$/, reply: () => ({ status: 'success', message: '', data: caseNumbers() }) },
  {
    path: /^\/manual-shifts\/by-patient\/([^/]+)$/,
    reply: ({ match }) => {
      const seen = new Map<string, any>();
      entries.filter((e) => e.patientId === match[1]).forEach((e) => {
        if (!seen.has(e.shiftId)) {
          seen.set(e.shiftId, {
            id: e.shiftId, caseNumber: String(e.caseNumber), startDate: e.originalStartDate, endDate: e.originalEndDate,
            type: 'manual', caregiverId: e.caregiverId, caregiverName: e.caregiverName, color: e.color,
          });
        }
      });
      return Array.from(seen.values());
    },
  },
  { path: /^\/manual-shifts\/by-caregiver\/([^/]+)$/, reply: () => [] },
  { path: /^\/manual-shifts\/([^/]+)\/revisions$/, reply: () => [] },
  // Real saved list filters (e.g. a caregiver filter) must not hide the demo rows
  { path: /^\/users\/me\/list-preferences$/, reply: () => ok({}) },
  { path: /^\/patients$/, reply: () => paginated(PATIENTS) },
  { path: /^\/patients\/([^/]+)$/, reply: ({ match }) => ok(PATIENTS.find((p) => p.id === match[1]) || PATIENTS[0]) },
  { path: /^\/caregivers$/, reply: () => paginated(CAREGIVERS) },
  { path: /^\/caregivers\/([^/]+)$/, reply: ({ match }) => ok(CAREGIVERS.find((c) => c.id === match[1]) || CAREGIVERS[0]) },
  { path: /^\/shifts\/[^/]+\/checklists(\/.*)?$/, reply: () => ok([]) },
  { method: 'post', path: /^\/shifts\/[^/]+\/checklists\/case\/batch$/, reply: () => ok({}) },
];

const k = (key: string) => `featurePreview.turnus.${key}`;

export const turnusTour: FeatureTour = {
  Page: TurnusPreviewPage,
  mocks,
  chapters: [
    {
      title: k('ch1'),
      steps: [
        { at: 0.5, target: 'sh-gantt', balloon: { title: k('hs1Title'), text: k('hs1Text') } },
        { at: 5, target: 'sh-block', balloon: { title: k('tourBlockTitle'), text: k('tourBlockText') } },
        { at: 9.5, target: 'sh-gap', balloon: { title: k('hs2Title'), text: k('hs2Text') } },
      ],
      duration: 14,
    },
    {
      title: k('ch2'),
      steps: [
        { at: 0.5, target: 'sh-new', balloon: { title: k('tourNewTitle'), text: k('tourNewText') } },
        { at: 5, target: 'sh-filters', balloon: { title: k('tourFiltersTitle'), text: k('tourFiltersText') } },
        { at: 9, target: 'sh-list', balloon: { title: k('tourListTitle'), text: k('tourListText') } },
      ],
      duration: 13,
    },
    {
      title: k('ch3'),
      steps: [
        { at: 0.5, target: 'sh-tab-wechsel', balloon: { title: k('tourTabTitle'), text: k('tourTabText') } },
        { at: 3, target: 'sh-tab-wechsel', action: 'click' },
        { at: 4, target: 'sh-changeovers', balloon: { title: k('hs3Title'), text: k('hs3Text') } },
        { at: 9, target: 'sh-changeovers', balloon: { title: k('tourExportTitle'), text: k('tourExportText') } },
        { at: 13, target: 'sh-tab-plan', action: 'click' },
      ],
      duration: 14,
    },
  ],
  savings: {
    aLabel: k('savA'), aDefault: 20, aMax: 300,
    bLabel: k('savB'), b: 20,
    toHoursPerMonth: (a, b) => (a * b) / 60,
  },
};
